import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, Flame, ArrowRight } from "lucide-react";

/*  /founding/success — post-Stripe confirmation for Founding Crew Pass.
    Polls the order until status flips to "paid", then reveals the
    buyer's unique seat number + next steps.
*/
const FoundingSuccess = () => {
  const [sp] = useSearchParams();
  const orderId   = sp.get("order_id")   || "";
  const sessionId = sp.get("session_id") || "";
  const [order, setOrder] = useState(null);
  const [err, setErr]     = useState("");

  useEffect(() => {
    if (!orderId) { setErr("Missing order id."); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const url = `/founding/order/${orderId}` + (sessionId ? `?session_id=${sessionId}` : "");
        const { data } = await api.get(url);
        if (cancelled) return;
        setOrder(data);
        if (data.status !== "paid" && attempts < 15) {
          setTimeout(poll, 1500);
        }
      } catch {
        if (!cancelled) setErr("Couldn't find your order. If payment went through, you'll still get the email.");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [orderId, sessionId]);

  if (err) return (
    <div className="min-h-screen bg-[#0a0908] text-[#f4f0ec] flex items-center justify-center px-6">
      <div className="text-center">
        <div className="text-sm text-[#8b0000] mb-2">⚠</div>
        <div className="text-sm">{err}</div>
        <Link to="/founding" className="text-[#d4af37] text-xs mt-4 inline-block">← Back to founding</Link>
      </div>
    </div>
  );

  if (!order) return (
    <div className="min-h-screen bg-[#0a0908] text-[#f4f0ec] flex items-center justify-center">
      <Loader2 className="animate-spin text-[#d4af37]" />
    </div>
  );

  const paid = order.status === "paid";

  return (
    <div className="min-h-screen bg-[#0a0908] text-[#f4f0ec] px-4 sm:px-6 py-10 sm:py-16" data-testid="founding-success">
      <div className="max-w-2xl mx-auto">
        {paid ? (
          <>
            <div className="text-center mb-10" data-testid="founding-success-paid">
              <CheckCircle2 size={48} className="text-[#d4af37] mx-auto mb-4" />
              <div className="text-[10px] tracking-[0.4em] text-[#d4af37] mb-2 flex items-center justify-center gap-2">
                <Flame size={12} /> FOUNDING CREW · WELCOME
              </div>
              <h1 className="font-display text-4xl sm:text-5xl text-[#f4f0ec] leading-tight mb-3">
                You're Seat #{order.seat_number}.
              </h1>
              <p className="text-sm text-[#a39797]">
                Lifetime access to Seasons 1–3. Founder pricing on everything after. A badge the rest of them can't buy.
              </p>
            </div>

            <div className="card-noir p-6 mb-6">
              <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-3">YOUR DOSSIER</div>
              <div className="space-y-2 text-sm text-[#d4d0cc]">
                <div className="flex justify-between"><span className="text-[#a39797]">Tier:</span> <span>{order.tier_label}</span></div>
                <div className="flex justify-between"><span className="text-[#a39797]">Seat number:</span> <span className="text-[#d4af37] font-semibold">#{order.seat_number}</span></div>
                <div className="flex justify-between"><span className="text-[#a39797]">Receipt email:</span> <span>{order.email}</span></div>
                {order.name && <div className="flex justify-between"><span className="text-[#a39797]">Detective name:</span> <span>{order.name}</span></div>}
              </div>
            </div>

            <div className="card-noir p-6 mb-6">
              <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-3">WHAT'S NEXT</div>
              <ol className="space-y-2 text-sm text-[#d4d0cc] list-decimal list-inside">
                <li>Your receipt + case library link just hit <span className="text-[#d4af37]">{order.email}</span></li>
                <li>Private Discord invite arrives within 24 hours (check spam)</li>
                <li>You get priority NYE access — the Last Alibi Standing invite drops Dec 20</li>
              </ol>
            </div>

            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Link to="/cases" className="btn-primary" data-testid="founding-success-cases">
                Open the case vault <ArrowRight size={14} />
              </Link>
              <Link to="/" className="btn-ghost text-xs" data-testid="founding-success-home">
                Back to home
              </Link>
            </div>
          </>
        ) : (
          <div className="text-center py-20" data-testid="founding-success-pending">
            <Loader2 size={28} className="text-[#d4af37] animate-spin mx-auto mb-4" />
            <div className="text-sm text-[#a39797]">Confirming your seat with Stripe…</div>
            <div className="text-[10px] text-[#5a5050] mt-2">This usually takes a few seconds.</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FoundingSuccess;
